class FreeGiftModule {
  constructor() {
    this.tiers = [];
    this.currentTier = null;
    this.selectedGifts = []; // ✅ UNIQUE variant ids as strings
    this._giftSyncTimer = null;
    this.moduleEnabled = true; // safe defaults when config element is absent
    this.moduleMode = 'both';
    this.isKiosk = false;
    this.init();
  }

  async init() {
    await this.loadTierConfig();
    await this.syncLocalStorageWithCart(); // ✅ ensure correct on load
    this.bindEvents();
    await this.updateProgressBars();
    await this.checkTierEligibility();
    this.updateUI();
    this.updateGiftList();
    this.updateFreeGiftVisibility();
  }

  /* =========================================================
     SAFE PARTIAL REFRESH (Cart page only)
     - Refresh ONLY fragments inside cart section (line-items + free-gift)
     - DOES NOT replace cart root (so Flickity upsell stays alive)
     - Fires BEFORE/AFTER events for other scripts
     ========================================================= */
  async refreshCartSection() {
    const cartRoot = document.querySelector('section[data-section-type="cart"][data-section-id]');
    if (!cartRoot) return; // cart page not present (drawer only) => theme will handle via cart:refresh

    const sectionId = cartRoot.getAttribute('data-section-id');
    if (!sectionId) return;

    // ✅ safest: fetch from root using sections param
    const url = `/?sections=${encodeURIComponent(sectionId)}`;

    let data;
    try {
      const res = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const text = await res.text();
      data = JSON.parse(text);
    } catch (e) {
      console.warn('[FreeGift] sections fetch/parse failed:', e);
      return;
    }

    const html = data[sectionId];
    if (!html) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');

    const fragmentNames = ["line-items", "free-gift"]; 

    fragmentNames.forEach((name) => {
      const currentEl = cartRoot.querySelector(`[data-cart-fragment="${name}"]`);
      const freshEl = doc.querySelector(`[data-cart-fragment="${name}"]`);
      if (!currentEl || !freshEl) return;

      document.dispatchEvent(new CustomEvent("freegift:beforeReplace", {
        detail: { el: currentEl, name }
      }));

      // ✅ swap content only (no node replacement)
      currentEl.innerHTML = freshEl.innerHTML;
    });

    // 🔔 AFTER: notify everyone cart DOM was refreshed
    document.dispatchEvent(new CustomEvent("cart:rerendered", { bubbles: true }));
  }

  /* =========================================================
     ✅ Theme-driven refresh (works for Cart Drawer + Cart Page)
     - Your theme already listens to cart:refresh (based on your upsell code)
     - This avoids manual DOM swapping inside drawer
     ========================================================= */
  async requestThemeCartRefresh() {
    // fire theme refresh (drawer/page)
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:refresh", { bubbles: true, detail: { scrollToTop: false } })
    );

    // wait until theme says rerendered (or timeout)
    await new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        document.removeEventListener("cart:rerendered", onDone, true);
        document.documentElement.removeEventListener("cart:rerendered", onDone, true);
        resolve();
      };
      const onDone = () => finish();

      document.addEventListener("cart:rerendered", onDone, true);
      document.documentElement.addEventListener("cart:rerendered", onDone, true);

      setTimeout(finish, 8000);
    });
  }

  async loadTierConfig() {
    const configElement = document.getElementById('free-gift-config');
    if (!configElement) return;

    let parsed;
    try {
      parsed = JSON.parse(configElement.textContent);
    } catch (e) {
      console.warn('[FreeGift] Invalid JSON in #free-gift-config', e);
      this.tiers = [];
      return;
    }

    if (Array.isArray(parsed) && parsed[0] && Array.isArray(parsed[0].detail)) {
      const root = parsed[0];
      this.tiers = (root.active === 'on') ? root.detail : [];
    }
    else if (parsed && Array.isArray(parsed.detail)) {
      this.tiers = (parsed.active === 'on' || !('active' in parsed)) ? parsed.detail : [];
    }
    
    else if (Array.isArray(parsed)) {
      this.tiers = parsed;
    } else {
      this.tiers = [];
    }

    this.tiers.sort((a, b) => parseFloat(a.limit) - parseFloat(b.limit));

    const modeEl = document.getElementById('free-gift-mode-config');
    if (modeEl) {
      try {
        const mc = JSON.parse(modeEl.textContent);
        this.moduleEnabled = mc.enabled !== false;
        this.moduleMode = mc.mode || 'both';
        this.isKiosk = mc.isKiosk === 'true' || mc.isKiosk === true;
      } catch (e) {
        console.warn('[FreeGift] Invalid JSON in #free-gift-mode-config', e);
      }
    }
  }

  shouldModuleBeActive() {
    if (!this.moduleEnabled) return false;
    if (this.moduleMode === 'both') return true;
    if (this.moduleMode === 'kiosk') return this.isKiosk;
    if (this.moduleMode === 'online') return !this.isKiosk;
    return true;
  }

  bindEvents() {
    // add gift click
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.add-gift-btn');
      if (btn) this.addGift(btn);
    });

    // cart updated event (your own dispatch or theme dispatch)
    document.addEventListener('cart:updated', async () => {
      await this.syncLocalStorageWithCart();
      await this.updateProgressBars();
      await this.checkTierEligibility();
      this.updateUI();
      this.updateGiftList();
      this.updateFreeGiftVisibility();
    });

    // after theme rerender: re-sync + re-bind UI (important for cart drawer)
    document.addEventListener('cart:rerendered', async () => {
      await this.syncLocalStorageWithCart();
      await this.updateProgressBars();
      await this.checkTierEligibility();
      this.updateUI();
      this.updateGiftList();
      this.updateFreeGiftVisibility();
    });

    // fallback when qty +/- clicked and theme doesn't dispatch cart:updated
    document.addEventListener('click', (e) => {
      const qtyBtn = e.target.closest('[data-action="decrease-quantity"], [data-action="increase-quantity"]');
      if (!qtyBtn) return;

      clearTimeout(this._giftSyncTimer);
      this._giftSyncTimer = setTimeout(async () => {
        await this.syncLocalStorageWithCart();
        await this.updateProgressBars();
        await this.checkTierEligibility();
        this.updateUI();
        this.updateGiftList();
      }, 700);
    });
  }

  isFreeGiftLineItem(item) {
    const v = item && item.properties ? item.properties._free_gift : null;
    return v === 'true' || v === true || v === '1' || v === 1;
  }

  /**
   * Cart total that counts toward the gift tiers, in dollars.
   * Excludes BCRS deposit lines (NAS080926, D4): the deposit must not unlock a gift.
   * Mirrors the Liquid calc in snippets/free-gift-progress.liquid.
   */
  getQualifyingTotal(cart) {
    const bcrs = (cart.items || [])
      .filter((i) => i.properties && (i.properties._bcrs === 'true' || i.properties._bcrs === true))
      .reduce((sum, i) => sum + (i.final_line_price || 0), 0);
    return (cart.total_price - bcrs) / 100;
  }

  async addGift(button) {
    await this.syncLocalStorageWithCart();

    const variantId = String(button.dataset.variantId || '');
    const maxQty = parseInt(button.dataset.maxQty, 10);

    if (this.selectedGifts.includes(variantId)) return;

    if (this.selectedGifts.length >= maxQty) {
      alert(`You have reached the maximum number of gifts (${maxQty}) for this tier`);
      return;
    }

    // === START LOADING ===
    document.dispatchEvent(new CustomEvent("theme:loading:start"));
    const loader = document.getElementById('loader-circle');
    if (loader) loader.style.display = 'block';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const formData = {
      items: [{
        id: variantId,
        quantity: 1,
        properties: {
          '_free_gift': 'true',
          '_gift_tier': this.currentTier ? this.currentTier.limit : '0',
          '_gift_tier_msg': this.currentTier ? this.currentTier.msg : ''
        }
      }]
    };

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData),
        credentials: 'same-origin'
      });

      if (!response.ok) throw new Error('Add gift failed');

      await this.requestThemeCartRefresh();

      await this.refreshCartSection();

      const cartResponse = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await cartResponse.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart, bubbles: true }));

    } catch (error) {
      console.error('Error adding gift:', error);
    } finally {
      // === END LOADING ===
      const loader2 = document.getElementById('loader-circle');
      if (loader2) loader2.style.display = 'none';

      document.dispatchEvent(new CustomEvent("theme:loading:end"));

      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  async updateProgressBars() {
    const cartResponse = await fetch('/cart.js', { credentials: 'same-origin' });
    const cart = await cartResponse.json();
    const cartTotal = this.getQualifyingTotal(cart);

    this.tiers.forEach((tier, index) => {
      const threshold = parseFloat(tier.limit);
      const progress = Math.min((cartTotal / threshold) * 100, 100);
      const progressBar = document.querySelector(`[data-tier-index="${index}"] .progress-fill`);
      if (progressBar) progressBar.style.width = `${progress}%`;
    });
  }

  async checkTierEligibility() {
    const cartResponse = await fetch('/cart.js', { credentials: 'same-origin' });
    const cart = await cartResponse.json();

    if (!this.shouldModuleBeActive()) {
      const giftsInCart = cart.items.filter(item => this.isFreeGiftLineItem(item));
      if (giftsInCart.length) {
        await this.removeAllGifts(giftsInCart);
        await this.requestThemeCartRefresh();
        await this.refreshCartSection();
        const r = await fetch('/cart.js', { credentials: 'same-origin' });
        const updated = await r.json();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: updated, bubbles: true }));
      }
      this.currentTier = null;
      this.toggleGiftSelector(false);
      return;
    }

    const cartTotal = this.getQualifyingTotal(cart);

    let eligibleTier = null;
    for (const tier of this.tiers) {
      if (cartTotal >= parseFloat(tier.limit)) eligibleTier = tier;
    }
    this.currentTier = eligibleTier;

    const freeGiftsInCart = cart.items.filter(item => this.isFreeGiftLineItem(item));
    let didRemove = false;

    if (!eligibleTier && freeGiftsInCart.length) {
      await this.removeAllGifts(freeGiftsInCart);
      didRemove = true;
    }

    if (eligibleTier && freeGiftsInCart.length > parseInt(eligibleTier.max_qty, 10)) {
      await this.removeExcessGifts(freeGiftsInCart, parseInt(eligibleTier.max_qty, 10));
      didRemove = true;
    }

    if (eligibleTier && freeGiftsInCart.length) {
      const removedMismatch = await this.removeMismatchedTierGifts(freeGiftsInCart, eligibleTier);
      if (removedMismatch) didRemove = true;
    }

    this.toggleGiftSelector(!!eligibleTier);

    if (didRemove) {
      await this.requestThemeCartRefresh();
      await this.refreshCartSection();

      const cartResponse2 = await fetch('/cart.js', { credentials: 'same-origin' });
      const newCart = await cartResponse2.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: newCart, bubbles: true }));
    }
    this.toggleGiftSelector(!!eligibleTier);
    this.updateSelectorVisibility();

  }

  updateSelectorVisibility() {
    const selectors = document.querySelectorAll('.free-gift-selector');
    if (!selectors.length) return;

    selectors.forEach(selector => {
      const container = selector.closest('#free-gift-block') || document;

      const giftCells = container.querySelectorAll('.fg-gift-cell');

      let hasVisibleGift = false;

      giftCells.forEach(cell => {
        if (cell.offsetParent !== null) {
          hasVisibleGift = true;
        }
      });

      selector.style.display = hasVisibleGift ? '' : 'none';
    });
  }



  // remove gifts only if gift tier > eligible tier (cart turun tier)
  async removeMismatchedTierGifts(gifts, eligibleTier) {
    const eligibleLimit = eligibleTier ? Number(eligibleTier.limit) : null;
    if (!eligibleLimit) return false;

    let didRemove = false;

    for (const gift of gifts) {
      const giftTierRaw = gift?.properties?._gift_tier;
      const giftTier = giftTierRaw != null ? Number(giftTierRaw) : null;

      if (giftTier && giftTier > eligibleLimit) {
        if (!gift || !gift.key) continue;

        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ id: gift.key, quantity: 0 }),
          credentials: 'same-origin'
        });

        didRemove = true;
      }
    }

    return didRemove;
  }

  // remove using id (line item key) so it won't break when lines shift
  async removeAllGifts(gifts) {
    for (const gift of gifts) {
      if (!gift || !gift.key) continue;

      await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: gift.key, quantity: 0 }),
        credentials: 'same-origin'
      });
    }

    this.selectedGifts = [];
    this.saveSelectedGifts();
  }

  //remove excess using id (line item key)
  async removeExcessGifts(gifts, maxAllowed) {
    const toRemove = gifts.slice(maxAllowed);

    for (const gift of toRemove) {
      if (!gift || !gift.key) continue;

      await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: gift.key, quantity: 0 }),
        credentials: 'same-origin'
      });
    }

    this.selectedGifts = this.selectedGifts.slice(0, maxAllowed);
    this.saveSelectedGifts();
  }

  toggleGiftSelector(show) {
    const selector = document.querySelector('.free-gift-selector');
    if (selector) selector.style.display = show ? 'block' : 'none';
  }

  updateUI() {
    const countDisplay = document.querySelector('.free-gift-selector h3');
    if (countDisplay && this.currentTier) {
      const maxQty = parseInt(this.currentTier.max_qty, 10);
      countDisplay.textContent = `Select your free gift (${this.selectedGifts.length}/${maxQty}):`;
    }
  }

  updateFreeGiftVisibility() {
    const block = document.getElementById('free-gift-block');
    if (!block) return;

    if (!this.currentTier || !this.tiers || !this.tiers.length) {
      block.style.display = 'none';
      return;
    }

    const lastTier = this.tiers[this.tiers.length - 1];
    const isLastTier = String(this.currentTier.limit) === String(lastTier.limit);

    const maxQty = parseInt(this.currentTier.max_qty, 10) || 0;
    const selectedCount = this.selectedGifts.length;

    if (isLastTier && selectedCount >= maxQty) {
      block.style.display = 'none';
    } else {
      block.style.display = '';
    }
  }

  // Hide gifts that already exist in cart
  updateGiftList() {
    const selectedSet = new Set(this.selectedGifts.map(String));

    document.querySelectorAll('.fg-gift-cell[data-variant-id]').forEach(cell => {
      const id = String(cell.dataset.variantId || '');
      if (!id) return;
      cell.style.display = selectedSet.has(id) ? 'none' : '';
    });
    this.updateSelectorVisibility();
  }

  saveSelectedGifts() {
    localStorage.setItem('freeGifts', JSON.stringify(this.selectedGifts));
  }

  loadSelectedGifts() {
    const saved = localStorage.getItem('freeGifts');
    this.selectedGifts = saved ? JSON.parse(saved) : [];
  }

  // UNIQUE gifts in cart
  async syncLocalStorageWithCart() {
    const cartResponse = await fetch('/cart.js', { credentials: 'same-origin' });
    const cart = await cartResponse.json();

    const gifts = cart.items
      .filter(item => this.isFreeGiftLineItem(item))
      .map(item => String(item.variant_id));

    this.selectedGifts = Array.from(new Set(gifts));
    this.saveSelectedGifts();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new FreeGiftModule();
});
